/**
 * Interdiction and drop feedback (M3 plan R10, spec §4.3 and §10), pure and allocation-free (D-014):
 * - the post feedback value: `feedback + pull × feedbackPull` while interdicted, and a short flash after a cruise
 *   drop (stronger out of a failed interdiction);
 * - a camera jitter of at most `jitterMaxDeg` (≤ 0.6°) while interdicted, from seeded smooth noise, rising with
 *   the pull meter.
 *
 * The feature writes `post.setFeedback(feedback, 0)` only when `stepInterdictionFx` says so: while the travel mode
 * is not `normal`, while a drop flash runs, and once more with 0 when both end, so M1's feedback is otherwise
 * untouched.
 */
import { createRng, nextFloat } from '../../core/rng'
import type { TravelMode } from '../../reach/runtime/types'
import type { InterdictionFxConfig } from './config'

/** Noise samples per axis (the jitter loops every `NOISE_SIZE / jitterHz` seconds). */
export const NOISE_SIZE = 64
const DEG = Math.PI / 180
const INV_SQRT3 = 1 / Math.sqrt(3)

export interface InterdictionFx {
  /** The value to hand `post.setFeedback` as its damage input (0..1). */
  feedback: number
  /** Jitter angles (radians) for the render camera; the rotation they make never exceeds `jitterMaxDeg`. */
  pitch: number
  yaw: number
  roll: number
  /** Drop flash: level and the remaining share of its duration (1 → 0). */
  flashLevel: number
  flash: number
  /** Noise clock (s). */
  t: number
  /** True while the last written feedback may be non-zero (one zeroing write follows). */
  wrote: boolean
  /** NOISE_SIZE values in −1..1 per axis (pitch, yaw, roll), interleaved. */
  readonly noise: Float32Array
}

export function createInterdictionFx(cfg: InterdictionFxConfig): InterdictionFx {
  const rng = createRng(cfg.seed)
  const noise = new Float32Array(NOISE_SIZE * 3)
  for (let i = 0; i < noise.length; i++) noise[i] = nextFloat(rng) * 2 - 1
  return { feedback: 0, pitch: 0, yaw: 0, roll: 0, flashLevel: 0, flash: 0, t: 0, wrote: false, noise }
}

/** Starts a drop flash: `interdicted` for a drop out of a failed interdiction (`REV.cruiseDrop` reason 3). */
export function triggerDropFlash(s: InterdictionFx, interdicted: boolean, cfg: InterdictionFxConfig): void {
  const level = interdicted ? cfg.interdictedDropFlash : cfg.dropFlash
  if (s.flash <= 0 || level >= s.flashLevel * s.flash) {
    s.flashLevel = level
    s.flash = 1
  }
}

/** Smoothly interpolated noise for `axis` (0 pitch, 1 yaw, 2 roll) at sample position `x`, in −1..1. */
export function sampleNoise(noise: Float32Array, axis: number, x: number): number {
  const f = Math.floor(x)
  const u = x - f
  const i0 = ((f % NOISE_SIZE) + NOISE_SIZE) % NOISE_SIZE
  const i1 = (i0 + 1) % NOISE_SIZE
  const a = noise[i0 * 3 + axis]
  const b = noise[i1 * 3 + axis]
  const w = u * u * (3 - 2 * u)
  return a + (b - a) * w
}

/**
 * Advances the feedback by `dt` for the travel `mode` and interdiction `pull` (0..1). Writes `feedback` and the
 * jitter angles. Returns true when the caller should call `post.setFeedback(s.feedback, 0)` this frame.
 */
export function stepInterdictionFx(s: InterdictionFx, mode: TravelMode, pull: number, dt: number, cfg: InterdictionFxConfig): boolean {
  const step = dt > 0 ? dt : 0
  const p = pull > 0 ? (pull < 1 ? pull : 1) : 0
  if (s.flash > 0) {
    s.flash -= step / cfg.dropFlashS
    if (s.flash < 0) s.flash = 0
  }
  const flash = s.flash * s.flashLevel

  if (mode === 'interdicted') {
    s.t += step
    const amp = cfg.jitterMaxDeg * DEG * (cfg.jitterBase + (1 - cfg.jitterBase) * p) * INV_SQRT3
    const x = s.t * cfg.jitterHz
    s.pitch = sampleNoise(s.noise, 0, x) * amp
    s.yaw = sampleNoise(s.noise, 1, x) * amp
    s.roll = sampleNoise(s.noise, 2, x) * amp
  } else {
    s.pitch = 0
    s.yaw = 0
    s.roll = 0
  }

  if (mode !== 'normal' || flash > 0) {
    let v = mode === 'interdicted' ? cfg.feedback + p * cfg.feedbackPull : 0
    if (flash > v) v = flash
    s.feedback = v < 1 ? v : 1
    s.wrote = true
    return true
  }
  s.feedback = 0
  if (s.wrote) {
    s.wrote = false
    return true
  }
  return false
}
